export const M5_UNIT_10 = {
  id: "m5-u10-questions-past",
  title: "Unit 10: Asking About the Past",
  topic: "Making questions about past events, vacations, and experiences",
  iconType: "question-circle",
  grammarImage: "/grammar/grammar-m5-u10.jpg",
  grammarText: "This unit teaches you how to ask questions about past events using the past simple. You'll learn to form yes/no questions with 'Did' (Did you visit Paris? Yes, I did / No, I didn't), and information questions with question words (When did you go? Where did you stay? What did you see?). This is essential for conversations about vacations, weekend activities, and past experiences. You combine the past simple verbs you already know with question structures to have natural conversations, interview someone about their trip, or talk about what you did.",

  sets: [
    // ===================================================================
    // SET 1: YES/NO QUESTIONS WITH "DID" (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u10-s1",
      title: "Set 1: Did You...?",
      questions: [
        {
          type: "match",
          q: "Match vacation vocabulary:",
          pairs: [
            { left: "Vacation US / Holiday UK", right: "Time off work/school for travel" },
            { left: "Trip", right: "Journey to a place" },
            { left: "Travel", right: "Go from place to place" }
          ],
          a: "completed",
          successtip: "✓ US: vacation/trip. UK: holiday. Both: travel (verb)"
        },
        {
          type: "mc",
          q: "STATEMENT: 'I went to Mexico.' QUESTION: '___ you go to Mexico?'",
          options: ["Did", "Do", "Was"],
          a: "Did",
          tip: "Past simple questions use 'Did' + base verb",
          successtip: "✓ DID for past questions. Went (statement) → Did...go (question)"
        },
        {
          type: "input",
          q: "FORM QUESTION: 'You visited Paris.' → '___ you visit Paris?' (question word)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Did + subject + base verb"
        },
        {
          type: "order",
          q: "Put in correct order:",
          words: ["Did", "you", "have", "a", "good", "vacation?"],
          a: "Did you have a good vacation?",
          successtip: "✓ 'Have a good vacation/trip' = common question"
        },
        {
          type: "mc",
          q: "ANSWER: 'Did you enjoy your trip?' 'Yes, ___.'",
          options: ["I did", "I enjoyed", "I was"],
          a: "I did",
          tip: "Short answer: Yes, I did / No, I didn't",
          successtip: "✓ Short answers avoid repetition: 'Yes, I did' (not 'Yes, I enjoyed')"
        },
        {
          type: "input",
          q: "NEGATIVE ANSWER: 'Did you visit the museum?' 'No, I ___.' (short form)",
          a: "didn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["did not"] },
          successtip: "✓ Didn't = did not. Both correct, didn't more common"
        },
        {
          type: "match",
          q: "Match questions to short answers:",
          pairs: [
            { left: "Did you go to the beach?", right: "Yes, I did" },
            { left: "Did they fly to Spain?", right: "No, they didn't" },
            { left: "Did she stay in a hotel?", right: "Yes, she did" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "CONVERSATION: 'Did you travel last summer?' '___. I stayed home.'",
          options: ["No, I didn't", "Yes, I did", "No, I don't"],
          a: "No, I didn't",
          tip: "Context: stayed home = didn't travel"
        },
        {
          type: "input",
          q: "FORM QUESTION: 'She took many photos.' → 'Did she ___ many photos?' (base verb)",
          a: "take",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "After 'did', use BASE FORM (not 'took'!)",
          successtip: "✓ Took (statement) → take (after did). Common error to avoid!"
        },
        {
          type: "order",
          q: "Traduce: ¿Visitaste el museo?",
          words: ["Did", "you", "visit", "the", "museum?"],
          a: "Did you visit the museum?"
        }
      ]
    },

    // ===================================================================
    // SET 2: MORE YES/NO QUESTIONS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u10-s2",
      title: "Set 2: Vacation Questions",
      questions: [
        {
          type: "match",
          q: "Match travel activities:",
          pairs: [
            { left: "Stay in hotel", right: "Accommodation" },
            { left: "Take photos", right: "Capture memories" },
            { left: "Try local food", right: "Experience cuisine" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "HOTEL: 'Did you ___ in a five-star hotel?'",
          options: ["stay", "stayed", "staying"],
          a: "stay",
          tip: "After 'did', always base form"
        },
        {
          type: "input",
          q: "PHOTOS: '___ you take a lot of photos on your trip?' (question word)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Ordena: food / try / you / Did / local / ?",
          words: ["Did", "you", "try", "local", "food?"],
          a: "Did you try local food?"
        },
        {
          type: "mc",
          q: "ANSWER: 'Did you like the food?' '___! It was delicious!'",
          options: ["Yes, I did", "Yes, I liked", "Yes, I was"],
          a: "Yes, I did"
        },
        {
          type: "input",
          q: "TRANSPORTATION: 'Did you ___ by plane or train?' (travel verb)",
          a: "go",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["travel", "fly"] }
        },
        {
          type: "match",
          q: "Match more vacation activities:",
          pairs: [
            { left: "Go sightseeing", right: "Visit tourist attractions" },
            { left: "Relax on beach", right: "Rest by ocean" },
            { left: "Buy souvenirs", right: "Get gifts/memories" }
          ],
          a: "completed",
          successtip: "✓ Sightseeing = tourism. Souvenirs = recuerdos (objects)"
        },
        {
          type: "mc",
          q: "SIGHTSEEING: 'Did you go ___?' 'Yes, we saw the Eiffel Tower and the Louvre!'",
          options: ["sightseeing", "sightsee", "sightsaw"],
          a: "sightseeing",
          tip: "'Go sightseeing' is the phrase",
          successtip: "✓ Go + -ing: go sightseeing, go swimming, go shopping"
        },
        {
          type: "input",
          q: "SOUVENIRS: 'Did you buy any souvenirs?' 'Yes, I ___. I bought magnets and postcards.'",
          a: "did",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Traduce: ¿Compraste regalos para tu familia?",
          words: ["Did", "you", "buy", "gifts", "for", "your", "family?"],
          a: "Did you buy gifts for your family?",
          successtip: "✓ Gifts = presents = regalos"
        }
      ]
    },

    // ===================================================================
    // SET 3: QUESTION WORDS - WHEN (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u10-s3",
      title: "Set 3: When Did You...?",
      questions: [
        {
          type: "match",
          q: "Match question words to what they ask:",
          pairs: [
            { left: "When", right: "Time (day, month, year)" },
            { left: "Where", right: "Place (city, country)" },
            { left: "What", right: "Thing/action" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PATTERN: '___ did you go to Paris?' 'Last summer.'",
          options: ["When", "Where", "What"],
          a: "When",
          tip: "'Last summer' = time answer → When question"
        },
        {
          type: "input",
          q: "TIME QUESTION: '___ did you arrive at the hotel?' 'At 6pm.' (time word)",
          a: "When",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["What time"] },
          successtip: "✓ 'When' or 'What time' both work for time questions"
        },
        {
          type: "order",
          q: "Ordena: back / When / home / you / come / did / ?",
          words: ["When", "did", "you", "come", "back", "home?"],
          a: "When did you come back home?",
          successtip: "✓ 'Come back' = return (volver)"
        },
        {
          type: "mc",
          q: "ANSWER: 'When did you visit Italy?' '___.'",
          options: ["In 2019", "To Rome", "By plane"],
          a: "In 2019",
          tip: "When → time answer (year, month, day)"
        },
        {
          type: "input",
          q: "DEPARTURE: 'When did you ___ for your vacation?' 'On July 5th.' (leave)",
          a: "leave",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["depart", "go"] },
          tip: "After 'did', base form"
        },
        {
          type: "match",
          q: "Match When questions to answers:",
          pairs: [
            { left: "When did you travel?", right: "Last month" },
            { left: "When did you return?", right: "Yesterday" },
            { left: "When did you book?", right: "Three weeks ago" }
          ],
          a: "completed",
          successtip: "✓ Ago = before now. 'Three weeks ago' = hace tres semanas"
        },
        {
          type: "mc",
          q: "AGO: 'When did you visit Mexico?' 'Two ___ ago.'",
          options: ["years", "year", "year's"],
          a: "years",
          tip: "'Two' = plural → years",
          successtip: "✓ Ago structure: number + time period + ago (one year ago, five days ago)"
        },
        {
          type: "input",
          q: "COMPLETE: 'When did they get married?' 'They got married ___ 2015.' (year preposition)",
          a: "in",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ Time prepositions: IN (year/month), ON (date), AT (time)"
        },
        {
          type: "order",
          q: "Traduce: ¿Cuándo llegaste al aeropuerto?",
          words: ["When", "did", "you", "arrive", "at", "the", "airport?"],
          a: "When did you arrive at the airport?"
        }
      ]
    },

    // ===================================================================
    // SET 4: QUESTION WORDS - WHERE (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u10-s4",
      title: "Set 4: Where Did You...?",
      questions: [
        {
          type: "match",
          q: "Match places:",
          pairs: [
            { left: "Hotel", right: "Where you sleep" },
            { left: "Restaurant", right: "Where you eat" },
            { left: "Beach", right: "Where you swim/relax" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PLACE: '___ did you stay?' 'In a hotel near the beach.'",
          options: ["Where", "When", "What"],
          a: "Where",
          tip: "'In a hotel' = place → Where question"
        },
        {
          type: "input",
          q: "DESTINATION: '___ did you go on vacation?' 'To Thailand.' (place word)",
          a: "Where",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Ordena: eat / Where / did / you / dinner / ?",
          words: ["Where", "did", "you", "eat", "dinner?"],
          a: "Where did you eat dinner?"
        },
        {
          type: "mc",
          q: "ANSWER: 'Where did you swim?' '___.'",
          options: ["At the beach", "Yesterday", "By bus"],
          a: "At the beach",
          tip: "Where → place answer"
        },
        {
          type: "input",
          q: "SHOPPING: 'Where did you buy that shirt?' 'I bought it ___ a market.' (place preposition)",
          a: "at",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["in"] },
          successtip: "✓ At/in both acceptable for markets/stores"
        },
        {
          type: "match",
          q: "Match Where questions to answers:",
          pairs: [
            { left: "Where did you stay?", right: "In a hostel" },
            { left: "Where did you fly from?", right: "From New York" },
            { left: "Where did you eat?", right: "At local restaurants" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PREPOSITIONS: 'Where did you go ___ vacation?' 'To Greece.'",
          options: ["on", "in", "at"],
          a: "on",
          tip: "'On vacation' is the phrase",
          successtip: "✓ On vacation, on holiday, on a trip (fixed expressions)"
        },
        {
          type: "input",
          q: "SPECIFICS: 'Where exactly did you stay in Paris?' 'We stayed in the ___ district.' (answer word)",
          a: "Marais",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Latin Quarter", "Montmartre", "center", "centre", "any district"] },
          tip: "Any Paris district works! No wrong answer here"
        },
        {
          type: "order",
          q: "Traduce: ¿Dónde tomaste esas fotos?",
          words: ["Where", "did", "you", "take", "those", "photos?"],
          a: "Where did you take those photos?"
        }
      ]
    },

    // ===================================================================
    // SET 5: QUESTION WORDS - WHAT (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u10-s5",
      title: "Set 5: What Did You...?",
      questions: [
        {
          type: "match",
          q: "What asks about:",
          pairs: [
            { left: "Activities", right: "What did you do?" },
            { left: "Things", right: "What did you see?" },
            { left: "Food", right: "What did you eat?" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ACTIVITIES: '___ did you do on vacation?' 'I went sightseeing and relaxed.'",
          options: ["What", "Where", "When"],
          a: "What",
          tip: "Activities/actions → What"
        },
        {
          type: "input",
          q: "SIGHTS: '___ did you see in Rome?' 'The Colosseum and Vatican.' (thing word)",
          a: "What",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Ordena: eat / did / What / for / you / dinner / ?",
          words: ["What", "did", "you", "eat", "for", "dinner?"],
          a: "What did you eat for dinner?"
        },
        {
          type: "mc",
          q: "FOOD: 'What did you try in Mexico?' '___.'",
          options: ["Tacos and guacamole", "Last week", "In Cancun"],
          a: "Tacos and guacamole",
          tip: "What → thing answer (food names)"
        },
        {
          type: "input",
          q: "EXPERIENCE: 'What was your favorite part of the trip?' 'My favorite part ___ the sunset cruise.' (past 'be')",
          a: "was",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match What questions to contexts:",
          pairs: [
            { left: "What did you do?", right: "Daily activities" },
            { left: "What did you buy?", right: "Shopping" },
            { left: "What did you think?", right: "Opinion" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "OPINION: 'What did you think of Paris?' 'I ___ it was beautiful!'",
          options: ["thought", "think", "was thinking"],
          a: "thought",
          tip: "Past question → past answer. Think→thought"
        },
        {
          type: "input",
          q: "PURCHASES: 'What did you buy as souvenirs?' 'I bought ___.' (any item)",
          a: "magnets",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["postcards", "t-shirts", "jewelry", "gifts", "anything"] },
          tip: "Any souvenir works! Be creative"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué hiciste el último día?",
          words: ["What", "did", "you", "do", "on", "the", "last", "day?"],
          a: "What did you do on the last day?",
          successtip: "✓ 'Last day' = último día. 'First day' = primer día"
        },
        {
          type: "mc",
          q: "COMPLETE: 'What did you enjoy most?' 'I enjoyed the ___ most.'",
          options: ["food", "eat", "ate"],
          a: "food",
          tip: "After 'the', use noun (not verb)"
        }
      ]
    },

    // ===================================================================
    // SET 6: QUESTION WORDS - HOW (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u10-s6",
      title: "Set 6: How Did You...?",
      questions: [
        {
          type: "match",
          q: "How asks about:",
          pairs: [
            { left: "Method", right: "How did you get there?" },
            { left: "Duration", right: "How long did you stay?" },
            { left: "Cost", right: "How much did it cost?" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "TRANSPORTATION: '___ did you get to the airport?' 'By taxi.'",
          options: ["How", "Where", "When"],
          a: "How",
          tip: "'By taxi' = method → How",
          successtip: "✓ Transportation: by plane, by train, by bus, by car, on foot"
        },
        {
          type: "input",
          q: "METHOD: 'How did you travel to Spain?' 'We traveled ___ plane.' (transportation preposition)",
          a: "by",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ By + vehicle (by car, by bus). But: ON foot (exception!)"
        },
        {
          type: "order",
          q: "Ordena: How / you / did / long / stay / ?",
          words: ["How", "long", "did", "you", "stay?"],
          a: "How long did you stay?",
          successtip: "✓ 'How long' = duration (¿Cuánto tiempo?)"
        },
        {
          type: "mc",
          q: "DURATION: 'How long was your vacation?' '___.'",
          options: ["Two weeks", "To Hawaii", "By plane"],
          a: "Two weeks",
          tip: "How long → time duration answer"
        },
        {
          type: "input",
          q: "PRICE: 'How much did the hotel ___?' 'It cost $100 per night.' (cost verb)",
          a: "cost",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Cost→cost (no change! Irregular)",
          successtip: "✓ Cost (present) = cost (past). Same form!"
        },
        {
          type: "match",
          q: "Match How questions to answers:",
          pairs: [
            { left: "How did you book it?", right: "Online" },
            { left: "How many photos did you take?", right: "About 500" },
            { left: "How was the weather?", right: "Sunny and hot" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "WEATHER: 'How was the weather in Thailand?' 'It ___ hot and humid.'",
          options: ["was", "is", "did"],
          a: "was",
          tip: "'How was...?' uses 'be' (was/were), not 'did'"
        },
        {
          type: "input",
          q: "QUANTITY: 'How many countries did you visit?' 'I visited ___ countries.' (number)",
          a: "five",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["5", "three", "3", "ten", "10", "any number"] }
        },
        {
          type: "order",
          q: "Traduce: ¿Cómo fue tu viaje?",
          words: ["How", "was", "your", "trip?"],
          a: "How was your trip?",
          successtip: "✓ 'How was...?' = opinion question (good/bad/amazing/terrible)"
        },
        {
          type: "mc",
          q: "BOOKING: 'How did you find your hotel?' 'I found it on ___.'",
          options: ["Booking.com", "Yesterday", "By taxi"],
          a: "Booking.com",
          tip: "'How did you find' = method of discovery"
        }
      ]
    },

    // ===================================================================
    // SET 7: QUESTION WORDS - WHO (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u10-s7",
      title: "Set 7: Who Did You...?",
      questions: [
        {
          type: "match",
          q: "Who asks about:",
          pairs: [
            { left: "People with you", right: "Who did you go with?" },
            { left: "People you met", right: "Who did you meet?" },
            { left: "People you saw", right: "Who did you see?" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "TRAVEL COMPANION: '___ did you go on vacation with?' 'With my family.'",
          options: ["Who", "What", "Where"],
          a: "Who",
          tip: "People → Who"
        },
        {
          type: "input",
          q: "COMPANY: '___ did you travel with?' 'I traveled alone.' (people word)",
          a: "Who",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Ordena: meet / Who / you / in / did / Paris / ?",
          words: ["Who", "did", "you", "meet", "in", "Paris?"],
          a: "Who did you meet in Paris?"
        },
        {
          type: "mc",
          q: "MEETING: 'Who did you see at the conference?' '___.'",
          options: ["I saw my old professor", "Last week", "In Boston"],
          a: "I saw my old professor",
          tip: "Who → person answer"
        },
        {
          type: "input",
          q: "GUIDE: 'Who ___ you the tour?' 'A local guide showed us around.' (past of 'give')",
          a: "gave",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["showed"] },
          tip: "Give→gave (irregular)"
        },
        {
          type: "match",
          q: "Match Who questions to contexts:",
          pairs: [
            { left: "Who did you stay with?", right: "Accommodation" },
            { left: "Who did you talk to?", right: "Conversations" },
            { left: "Who took the photo?", right: "Photography" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PHOTOGRAPHER: 'Who took this amazing photo?' '___.'",
          options: ["My friend took it", "At the beach", "Yesterday"],
          a: "My friend took it",
          tip: "Who → person did the action"
        },
        {
          type: "input",
          q: "ALONE VS GROUP: 'Did you travel alone or with someone?' 'I traveled ___ my boyfriend.' (preposition)",
          a: "with",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Traduce: ¿Con quién fuiste al concierto?",
          words: ["Who", "did", "you", "go", "to", "the", "concert", "with?"],
          a: "Who did you go to the concert with?",
          successtip: "✓ 'Who...with?' is informal. Formal: 'With whom did you go?' (rare)"
        },
        {
          type: "mc",
          q: "CONTEXT: 'Who did you invite to your party?' 'I ___ all my friends.'",
          options: ["invited", "invite", "am inviting"],
          a: "invited",
          tip: "Past question → past answer"
        }
      ]
    },

    // ===================================================================
    // SET 8: MIXED QUESTION WORDS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u10-s8",
      title: "Set 8: Conversation Practice",
      questions: [
        {
          type: "match",
          q: "Quick review - Match questions:",
          pairs: [
            { left: "When did you...?", right: "Time" },
            { left: "Where did you...?", right: "Place" },
            { left: "What did you...?", right: "Thing/action" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PICK CORRECT: 'A: ___ did you go?' 'B: To Italy.'",
          options: ["Where", "When", "What"],
          a: "Where",
          tip: "Answer = place → Where"
        },
        {
          type: "input",
          q: "FILL: 'A: ___ did you arrive?' 'B: Yesterday evening.' (time word)",
          a: "When",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Ordena natural dialogue:",
          words: ["What", "did", "you", "do", "there?"],
          a: "What did you do there?"
        },
        {
          type: "mc",
          q: "CONVERSATION: 'A: How was your trip?' 'B: It ___ amazing!'",
          options: ["was", "did", "had"],
          a: "was",
          tip: "'How was...?' → answer with 'was'"
        },
        {
          type: "input",
          q: "DIALOGUE: 'A: How long did you stay?' 'B: We stayed ___ two weeks.' (duration word)",
          a: "for",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ For + duration: for two weeks, for three days, for a month"
        },
        {
          type: "match",
          q: "Match conversation pairs:",
          pairs: [
            { left: "Did you have fun?", right: "Yes, I did!" },
            { left: "Who did you go with?", right: "With my sister" },
            { left: "How much did it cost?", right: "About $2000" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "NATURAL FLOW: 'Where did you stay?' 'In a nice hotel.' '___ was it?' 'It was great!'",
          options: ["How", "What", "When"],
          a: "How",
          tip: "Opinion question follows → How"
        },
        {
          type: "input",
          q: "FOLLOW-UP: 'What did you eat?' 'Sushi.' 'Did you ___ it?' 'Yes, I loved it!' (like)",
          a: "like",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["enjoy"] }
        },
        {
          type: "order",
          q: "Complete vacation interview sequence:",
          words: ["When", "did", "you", "go?", "Where", "did", "you", "stay?", "What", "did", "you", "do?"],
          a: "When did you go? Where did you stay? What did you do?",
          successtip: "✓ Natural interview order: When → Where → What (time → place → activities)"
        },
        {
          type: "mc",
          q: "REAL CONVERSATION: 'So, tell me about your trip!' Which is the best first question?",
          options: ["Where did you go?", "How much did you spend?", "What time did you leave?"],
          a: "Where did you go?",
          tip: "Start general (destination), then get specific"
        }
      ]
    },

    // ===================================================================
    // SET 9: VACATION STORYTELLING (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u10-s9",
      title: "Set 9: Telling Your Story",
      questions: [
        {
          type: "match",
          q: "Vacation story structure:",
          pairs: [
            { left: "Opening", right: "Where/when you went" },
            { left: "Details", right: "What you did/saw" },
            { left: "Conclusion", right: "How it was/opinion" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "START STORY: 'Last summer I went to Japan. I ___ there for two weeks.'",
          options: ["stayed", "stay", "staying"],
          a: "stayed",
          tip: "Past story → all past verbs"
        },
        {
          type: "input",
          q: "ACTIVITIES: 'I stayed in Tokyo. I ___ temples, ate amazing food, and took many photos.' (visit)",
          a: "visited",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build a story sentence:",
          words: ["I", "went", "to", "Paris", "with", "my", "family", "in", "June"],
          a: "I went to Paris with my family in June",
          successtip: "✓ Story order: destination + companion + time"
        },
        {
          type: "mc",
          q: "ASKING ABOUT THEIR STORY: 'That sounds amazing! ___ did you like most?'",
          options: ["What", "When", "Who"],
          a: "What",
          tip: "Favorite thing → What"
        },
        {
          type: "input",
          q: "ANSWERING: 'What did you like most?' 'I liked the ___ most.' (your answer)",
          a: "food",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["culture", "people", "beaches", "weather", "anything"] }
        },
        {
          type: "match",
          q: "Match story questions to responses:",
          pairs: [
            { left: "Where did you go?", right: "To Thailand" },
            { left: "How long were you there?", right: "For 10 days" },
            { left: "Would you go back?", right: "Definitely!" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "RECOMMENDATION: 'Would you recommend it?' '___! Everyone should visit.'",
          options: ["Absolutely", "Never", "Maybe not"],
          a: "Absolutely",
          tip: "Strong positive → Absolutely"
        },
        {
          type: "input",
          q: "PROBLEMS: 'Did you have any problems?' 'Yes, I ___ my wallet on the first day!' (lose)",
          a: "lost",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Lose→lost (irregular)"
        },
        {
          type: "order",
          q: "Traduce vacation summary: Fue increíble y quiero volver.",
          words: ["It", "was", "amazing", "and", "I", "want", "to", "go", "back"],
          a: "It was amazing and I want to go back",
          successtip: "✓ 'Go back' = return (volver)"
        },
        {
          type: "mc",
          q: "COMPLETE EXCHANGE: 'When are you going again?' 'I don't know, but I ___ go next summer.'",
          options: ["might", "did", "went"],
          a: "might",
          tip: "Future possibility → might/may",
          successtip: "✓ Might = maybe will (quizás). Shows uncertainty"
        }
      ]
    },

    // ===================================================================
    // SET 10: INTERVIEW PRACTICE (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u10-s10",
      title: "Set 10: Full Conversation",
      questions: [
        {
          type: "match",
          q: "Conversation skills:",
          pairs: [
            { left: "Ask follow-up questions", right: "Keep conversation going" },
            { left: "Show interest", right: "'That sounds great!'" },
            { left: "Share your experience", right: "'I went there too!'" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "OPENING: 'Hi! Did you have a good weekend?' Best response:",
          options: ["Yes! I went to the beach. How about you?", "Yes.", "I did."],
          a: "Yes! I went to the beach. How about you?",
          tip: "Give details + ask back = good conversation",
          successtip: "✓ 'How about you?' = reciprocal interest (conversational skill)"
        },
        {
          type: "input",
          q: "SHOW INTEREST: 'I went to Iceland!' Response: 'Wow! ___ did you see the Northern Lights?'",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Natural follow-up question order:",
          words: ["That's", "cool!", "How", "long", "were", "you", "there?"],
          a: "That's cool! How long were you there?"
        },
        {
          type: "mc",
          q: "COMPARISON: 'I visited Rome.' 'Really? ___ too! When did you go?'",
          options: ["I went there", "I go there", "I'm going there"],
          a: "I went there",
          tip: "Match their tense (past)"
        },
        {
          type: "input",
          q: "SPECIFIC DETAIL: 'Where exactly in Rome did you stay?' 'I stayed near the ___.' (famous place)",
          a: "Colosseum",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Vatican", "Trevi Fountain", "Spanish Steps", "any landmark"] }
        },
        {
          type: "match",
          q: "Interview someone about their trip - question sequence:",
          pairs: [
            { left: "1st: Destination", right: "Where did you go?" },
            { left: "2nd: Duration", right: "How long did you stay?" },
            { left: "3rd: Activities", right: "What did you do there?" }
          ],
          a: "completed",
          successtip: "✓ Logical flow: destination → duration → activities → opinion"
        },
        {
          type: "mc",
          q: "RECOMMENDATION QUESTION: 'I'm thinking of going to Japan. ___'",
          options: ["Did you like it?", "Do you like it?", "Will you like it?"],
          a: "Did you like it?",
          tip: "They already went (past) → Did"
        },
        {
          type: "input",
          q: "ADVICE: 'What should I do there?' 'You ___ definitely visit the temples!' (should)",
          a: "should",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "End conversation politely:",
          words: ["Thanks", "for", "the", "tips!", "I", "can't", "wait", "to", "go!"],
          a: "Thanks for the tips! I can't wait to go!",
          successtip: "✓ 'Can't wait' = very excited (no puedo esperar)"
        },
        {
          type: "mc",
          q: "FINAL: You've mastered asking about the past! Which skill is MOST important?",
          options: ["Asking follow-up questions to keep conversation flowing", "Memorizing questions", "Speaking fast"],
          a: "Asking follow-up questions to keep conversation flowing",
          tip: "Conversation = back and forth, not interview!",
          successtip: "✓ Natural conversation = listen → respond → ask new question (flow, not script!)"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - 3 PARAGRAPHS (10 questions)
  // ===================================================================
  // ===================================================================
// READING SET: Interview About a Trip
// ===================================================================
readingSet: {
  id: "m5-u10-reading",
  title: "Reading: Interview About a Trip",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Big Trip (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**TRAVEL BLOG INTERVIEW**
      
We talked to Sarah about her recent trip to Thailand.

**Blogger:** Hi Sarah! Where did you go on vacation?
**Sarah:** I went to Thailand! I visited Bangkok, Chiang Mai, and some islands in the south.

**Blogger:** Wow! How long did you stay?
**Sarah:** I stayed for three weeks. It was the perfect amount of time to see everything I wanted.`
    },
    {
      type: "mc",
      q: "Where did Sarah go for her vacation?",
      options: ["Thailand", "China", "Vietnam"],
      a: "Thailand",
      successtip: "✓ THAILAND! Destination confirmed! 🇹🇭"
    },
    {
      type: "input",
      q: "How many cities or places did Sarah mention visiting?",
      a: "three",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["3", "three places"] },
      successtip: "✓ THREE! Bangkok, Chiang Mai, and the islands! 📍📍📍"
    },
    {
      type: "mc",
      q: "What was the total duration of her stay?",
      options: ["Three weeks", "Two weeks", "One month"],
      a: "Three weeks",
      successtip: "✓ THREE WEEKS! Long enough to see everything! 🗓️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Activities & Places (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Activities in the North and South**

**Blogger:** What did you do in Bangkok?
**Sarah:** I visited temples, tried amazing street food, and went to floating markets. The city is so lively!

**Blogger:** And in Chiang Mai?
**Sarah:** I took a cooking class and learned to make pad thai and green curry. I also went to an elephant sanctuary.

**Blogger:** What about the islands?
**Sarah:** I stayed on Koh Phi Phi and Koh Samui. I went snorkeling, relaxed on the beach, and watched beautiful sunsets every evening.`
    },
    {
      type: "mc",
      q: "What new skill did Sarah learn in Chiang Mai?",
      options: ["How to cook Thai food", "How to ride elephants", "Thai language"],
      a: "How to cook Thai food",
      successtip: "✓ THAI COOKING! Pad thai and green curry! 🍜🔥"
    },
    {
      type: "input",
      q: "Which specific island did she mention visiting? (Name one)",
      a: "Koh Phi Phi",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Koh Samui", "Phi Phi", "Samui"] },
      successtip: "✓ Correct! She visited Koh Phi Phi and Koh Samui! 🏝️"
    },
    {
      type: "mc",
      q: "Which water-based activity did Sarah enjoy on the islands?",
      options: ["Snorkeling", "Surfing", "Kayaking"],
      a: "Snorkeling",
      successtip: "✓ SNORKELING! Seeing the fish and coral! 🤿🐠"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Logistics & Feelings (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Final Details**

**Blogger:** How did you travel between cities?
**Sarah:** I flew from Bangkok to Chiang Mai, then took a train back to Bangkok. From there, I took a ferry to the islands.

**Blogger:** How much did the whole trip cost?
**Sarah:** About $1,500, including flights, hotels, food, and activities. Thailand is really affordable!

**Blogger:** Would you go back?
**Sarah:** Absolutely! I fell in love with the culture, the people, and especially the food. I want to explore the northern regions next time.`
    },
    {
      type: "mc",
      q: "How did Sarah get to Chiang Mai from Bangkok?",
      options: ["By plane", "By train", "By bus"],
      a: "By plane",
      successtip: "✓ BY PLANE! She flew there! ✈️"
    },
    {
      type: "input",
      q: "What was the approximate total cost of the trip in dollars?",
      a: "1500",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["$1,500", "1,500", "1500 dollars"] },
      successtip: "✓ $1,500! A great deal for a 3-week trip! 💰"
    },
    {
      type: "mc",
      q: "Does Sarah recommend returning to Thailand?",
      options: ["Yes, absolutely", "No", "Maybe"],
      a: "Yes, absolutely",
      successtip: "✓ ABSOLUTELY! She fell in love with the country! ❤️"
    },
    {
      type: "mc",
      q: "COMPREHENSION: This text structure is an example of:",
      options: ["A natural vacation interview using WH- questions", "A travel advertisement", "A history lesson"],
      a: "A natural vacation interview using WH- questions",
      successtip: "✓ Perfect comprehension! It uses When, Where, What, and How to tell a story! 🌟"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u10-review",
    title: "Unit 10 Review: Past Questions Mastery",
    questions: [
      {
        type: "match",
        q: "Review question words:",
        pairs: [
          { left: "When", right: "Time" },
          { left: "Where", right: "Place" },
          { left: "What", right: "Thing/action" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "YES/NO: '___ you go to the party?' 'Yes, I did.'",
        options: ["Did", "Do", "Was"],
        a: "Did"
      },
      {
        type: "input",
        q: "WHEN: '___ did you arrive?' 'Yesterday.' (time word)",
        a: "When",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "WHERE: '___ did you stay?' 'In a hotel.'",
        options: ["Where", "When", "What"],
        a: "Where"
      },
      {
        type: "order",
        q: "Build question:",
        words: ["What", "did", "you", "do", "there?"],
        a: "What did you do there?"
      },
      {
        type: "input",
        q: "HOW: '___ did you get there?' 'By plane.' (method word)",
        a: "How",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "WHO: '___ did you go with?' 'With my sister.'",
        options: ["Who", "What", "Where"],
        a: "Who"
      },
      {
        type: "match",
        q: "Match questions to answer types:",
        pairs: [
          { left: "How long?", right: "Duration (two weeks)" },
          { left: "How much?", right: "Cost ($500)" },
          { left: "How was?", right: "Opinion (amazing)" }
        ],
        a: "completed"
      },
      {
        type: "input",
        q: "BASE FORM: 'Did you ___ the museum?' (visit)",
        a: "visit",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ After 'did', always BASE FORM (not visited!)"
      },
      {
        type: "mc",
        q: "FINAL: Complete vacation interview: 'Where did you go?' 'To Spain.' '___' 'For two weeks.' 'What did you do?' 'I visited museums and tried tapas!'",
        options: ["How long did you stay?", "What did you eat?", "Who did you see?"],
        a: "How long did you stay?",
        successtip: "✓ Perfect! You can now interview anyone about their vacation, weekend, or past experience using all question types! 🎉✈️"
      }
    ]
  }
};
